import { sleep } from '../common-utils';
import { TYPES } from '../render/constants';
import { layoutTextNode } from '../render/text';
import { once } from '../render/utils/dom';
import { charWidth } from '../render/utils/text';
import { Provider } from './provider';

type FontLoaderLink = HTMLLinkElement & {
  p: Promise<boolean>;
};

/**
 * Find the font by name
 * @returns
 */
export function findFontByName(name: string, fonts: Font[]) {
  for (const family of fonts) {
    for (const font of family.list) {
      if (font.name === name) {
        return font;
      }
    }
  }

  return null;
}

function normalizeName(name: string) {
  return name.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9_-]/g, '_');
}

/**
 * Load the font, it will retry until success or retry times is used up.
 * @param font
 * @param retryTimes default is 3
 * @param timeout in ms, if specified, it will consider it as failed if timeout, default to null
 * @returns
 */
export async function loadFont(
  font: Font['list'][number],
  retryTimes: number = 3,
  timeout?: number,
): Promise<boolean> {
  if (!font.url) return false;

  const name = normalizeName(font.name);
  const existedLink = document.querySelector(`link#font_${name}`) as FontLoaderLink;

  if (existedLink) {
    if (existedLink.p) {
      return timeout
        ? Promise.race([sleep(timeout).then(() => false), existedLink.p])
        : existedLink.p;
    }
    if (existedLink.dataset.loaded === 'true') return Promise.resolve(true);

    // make sure all named link is handled by `loadFont`
    existedLink.remove();
  }

  const createLink = () => {
    const link = document.createElement('link');
    link.href = font.url!;
    link.rel = 'stylesheet';
    link.id = `font_${name}`;

    document.head.appendChild(link);
    return link as FontLoaderLink;
  };

  let resolveWaited: undefined | ((value: boolean) => void) = undefined;
  const waitLinkLoaded = (link: FontLoaderLink) => {
    return new Promise((resolve) => {
      const dispose: (() => void)[] = [];
      resolveWaited = (val: boolean) => {
        if (val) {
          link.dataset.loaded = 'true';
        }
        resolve(val);
        dispose.forEach((d) => d());
        resolveWaited = undefined;
      };
      dispose.push(
        once(link, 'load', () => resolveWaited?.(true)),
        once(link, 'error', () => resolveWaited?.(false)),
      );
    });
  };

  let resolveLoaded!: (value: boolean) => void;
  let settled = false;
  const p = new Promise<boolean>((r) => (resolveLoaded = r));
  const resolve = (val) => {
    if (!settled) {
      settled = true;
      resolveLoaded(val);
    }
  };
  let link;

  if (timeout !== undefined) {
    sleep(timeout).then(() => {
      if (!settled) {
        if (resolveWaited) {
          resolveWaited(false);
        } else {
          resolve(false);
        }
        retryTimes = 0;
        link?.remove();
      }
    });
  }

  const tryLoad = async () => {
    while (retryTimes > 0) {
      link = createLink();
      link.p = p;
      const success = await waitLinkLoaded(link);

      if (success) {
        return resolve(true);
      } else {
        retryTimes--;
        link.remove();
      }
    }

    resolve(false);
  };

  tryLoad().catch(() => resolve(false));

  return p;
}

export function isFontLoaded(font: Font['list'][number] | string) {
  if (!font) return true;
  const name = normalizeName(typeof font === 'string' ? font : font.name);
  const link = document.querySelector(`link#font_${name}`) as HTMLLinkElement;
  return link?.dataset.loaded === 'true';
}

export type Font = {
  /**
   * Not actually the name, used for language grouping
   */
  name: string;
  list: {
    /**
     * The font name used in actual CSS or rendering
     */
    name: string;
    /**
     * The font name shown to users
     */
    label: string;
    /**
     * The font URL, it might be empty if the font comes from system.
     * E.g., Arial
     */
    url?: string;
    /**
     * Used to categorize fonts base on their visual style
     */
    style: string;

    /**
     * Font weight the font supports
     */
    weights: {
      /**
       * E.g., Thin, Regular, Bold
       */
      name: string;
      /**
       * The numeric weight value, e.g., 100, 400, 700
       */
      value: string;
    }[];
  }[];
};

export class FontProvider extends Provider {
  private _cache: Record<string, unknown>;
  fonts: Font[];

  constructor(fonts: Font[] = []) {
    super();
    this._cache = {};
    this.fonts = fonts;
  }

  flush() {
    if (this.disposed) return;
    const { renderContext } = this;
    renderContext.api.flushFontCache();
    renderContext.api
      .getNodes()
      .filter((node) => node.type === TYPES.text)
      .map((node) => {
        layoutTextNode(node);
        renderContext.api.markNodeUpdate(node);
      });
    renderContext.api.markNeedRender();
    renderContext.api.paint();
  }

  attach(renderContext) {
    super.attach(renderContext);
    renderContext.api.flushFontCache = () => {
      renderContext.cache.font.clear();
      renderContext.cache.fontCheck.clear();
      charWidth.clear();
    };

    renderContext.api.loadAllFonts = () => {
      return this.loads(this.fonts.map((type) => type.list.map((v) => v.name)).flat());
    };

    renderContext.api.loadFonts = (fontName) => {
      if (Array.isArray(fontName)) {
        return this.loads(fontName);
      } else {
        return this.loads([fontName]);
      }
    };

    renderContext.api.loadFontsInUsed = async () => {
      const debug = this.renderContext.debug;
      const startAt = Date.now();
      const debugTag = '[Font Debug] [FIU]';
      if (debug) {
        console.info(`${debugTag} start...`);
      }
      const texts = renderContext.api.getNodes().filter((node) => node.type === TYPES.text);
      const set = new Set();
      texts.map((node) => set.add(node.fontFamily));
      if (debug) {
        console.info(
          `${debugTag} Found ${set.size} unique fonts in use: ${Array.from(set).join(', ')}`,
        );
      }
      try {
        await this.loads(Array.from(set));
        if (debug) {
          console.info(`${debugTag} loaded in ${Math.round(Date.now() - startAt)}ms`);
        }
      } catch (e) {
        if (debug) {
          console.error(`${debugTag} Error loading fonts: ${e.message}`, e);
          console.info(`${debugTag} Failed after ${Math.round(Date.now() - startAt)}ms`);
        }
      }
    };

    this.flush = () => {
      if (this.disposed) return;
      renderContext.api.flushFontCache();
      renderContext.api
        .getNodes()
        .filter((node) => node.type === TYPES.text)
        .map((node) => {
          layoutTextNode(node);
          renderContext.api.markNodeUpdate(node);
        });
      renderContext.api.markNeedRender();
      renderContext.api.paint();
    };
    if (document.fonts) {
      document.fonts.addEventListener('loadingdone', this.flush);
    }
  }

  async loads(fontNames) {
    fontNames.forEach((name) => this.load(name));
  }

  getFont(fontName) {
    for (const category of this.fonts) {
      const font = category.list.find((f) => f.name === fontName);
      if (font) {
        return font;
      }
    }
    return null;
  }

  async load(fontName) {
    try {
      const start = Date.now();
      const font = this.getFont(fontName);
      const result = await this._load(font);
      if (this.renderContext.debug) {
        console.info(
          `[Font Debug] Font loaded successfully: ${fontName} ${Math.round(Date.now() - start)}ms`,
        );
      }
      return result;
    } catch (e) {
      if (this.renderContext.debug) {
        console.error(`[Font Debug] Error loading font ${fontName}:`, e);
      }
      return null;
    }
  }

  async _load(font) {
    if (!font) return;
    if (this._cache[font.name]) return;
    if (font.content) {
      return this.loadStyle(font);
    }
    const success = await loadFont(font);
    if (success) {
      this._cache[font.name] = true;
      if (this.flush) {
        this.flush();
      }
    }
    return success;
  }

  loadStyle(font) {
    const content = font.content;
    const link = document.createElement('style');
    link.textContent = content;
    link.id = `font_${font.name}`;
    document.head.appendChild(link);
  }

  get(src) {
    const item = this._cache[src];
    if (item) {
      return item;
    }
    return null;
  }

  remove(src) {
    if (this._cache[src]) {
      delete this._cache[src];
    }
  }

  dispose() {
    super.dispose();
    this._cache = {};
    if (document.fonts) {
      document.fonts.removeEventListener('loadingdone', this.flush);
    }
  }

  clear() {
    this._cache = {};
  }
}
